// --- Core ---
import {ReactElement} from "react";
import {FieldRenderProps} from "react-final-form";
import {every, reject} from "underscore";
// --- Material UI ---
import {Autocomplete, CircularProgress} from "@mui/material";
import {AutocompleteProps, createFilterOptions} from "@mui/material/Autocomplete";
// --- SaniQ ---
import {DeserializedRecord} from "../../../models/Helpers";
import {SaniQTextFieldProps, TextField} from "helpers/forms/fields/TextField";

interface AutocompleteOption {
  key: string | number;
  text: string;
}

interface SlotProps {
  textField?: Partial<SaniQTextFieldProps>;
}

interface BaseProps<T> {
  defaultOption?: T;
  hideEmptyValue?: boolean;
  inputInOptions?: boolean;
  label: ReactElement;
  required?: boolean;
  slotProps: SlotProps;
}

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
type AutocompleteTextFieldProps<T, FreeSolo extends boolean> =
  PartialBy<
    FieldRenderProps<T>
    & AutocompleteProps<T, false, undefined, FreeSolo>,
    "renderInput"
  >;

const isRelationOptionEqualToValue: (option: DeserializedRecord, value: DeserializedRecord) => boolean = (option, value) => option.id === value.id;
//@ts-expect-error The typing of the Autocomplete component is to complex -.- to handle now
const autocompleteTextFieldFilter = createFilterOptions<AutocompleteOption>({stringify: option => option.text || option});

/**
 * AutocompleteTextField component.
 */
const AutocompleteTextField = <T = AutocompleteOption, FreeSolo extends boolean = false>(
  {
    options,
    // @ts-expect-error To use the component outside a final form
    input = {},
    label,
    loading,
    meta = {},
    required,
    defaultOption,
    hideEmptyValue = false,
    inputInOptions,
    slotProps = {},
    ...custom
  }: AutocompleteTextFieldProps<T, FreeSolo> & BaseProps<T>
) => (
  <Autocomplete<T, false, undefined, FreeSolo>
      fullWidth clearOnBlur
      options={inputInOptions || (!hideEmptyValue && !required && input.value === "") ? [...options, input.value] : options}
      renderInput={
        (params) => (
          //@ts-expect-error The input prop is provided through params
          <TextField
            label={label}
            meta={meta}
            required={required}
            endAdornment={loading && <CircularProgress color="inherit" size={16}/>}
            {...slotProps.textField}
            {...params}
          />
        )
      }
      //@ts-expect-error The typing of the Autocomplete component is to complex -.- to handle now
      getOptionLabel={(option) => option.text || ""}
      //@ts-expect-error The typing of the Autocomplete component is to complex -.- to handle now
      isOptionEqualToValue={(option, value) => option.key === value.key}
      //@ts-expect-error The filterOptions just work
      filterOptions={
        (filterOptions, state) => {
          //@ts-expect-error The typing of the Autocomplete component is to complex -.- to handle now
          let filtered = autocompleteTextFieldFilter(filterOptions, state);
          //@ts-expect-error The typing of the Autocomplete component is to complex -.- to handle now
          filtered = reject(filtered, (option) => option === "");
          if (state.inputValue !== "" && every(filtered, (option) => (option.text.toLowerCase() !== state.inputValue.toLowerCase()))) {
            if (inputInOptions) {
              filtered.push({key: null, text: state.inputValue});
            }
          }

          return filtered;
        }
      }
      {...input}
      onChange={
        (_event, option, reason) => {
          let actualOption = option;

          if (defaultOption && reason === "clear") {
            actualOption = defaultOption;
          }
          input.onChange(actualOption);
        }
      }
      {...custom}
    />
  );

export {
  AutocompleteOption,
  AutocompleteTextField,
  isRelationOptionEqualToValue
};
